import { useEffect, useState } from 'react';

import isEmpty from 'lodash/isEmpty';

import { EFlagPartner, TFeatureFlagApiResponse } from '..';

import api from './api';

export type TUseFeatureFlag = {
  division: string;
  vertical: string;
  partner: EPartner;
  environment: EEnvironment;
  featureFlagUrl?: string;
  flags: TFeatureFlagApiResponse | null;
  setFlags: (value: TFeatureFlagApiResponse) => void;
  status: EStatus;
  setStatus: (value: EStatus) => void;
  flagsToCheck?: string[];
};

export type EEnvironment = 'qa' | 'sandbox' | 'production';

export type EPartner = 'toggle' | 'toyota' | 'better';

export type EStatus = 'PENDING' | 'SUCCESS' | 'FAILED';

const partnerMap: Record<EPartner, EFlagPartner> = {
  toggle: 'tog',
  toyota: 'tai',
  better: 'bet',
};

/* 
  division: string - berry | mango | etc.
  veritcal: string - auto | renters | homeowners
  partner: EPartner
  environment: EEnvironment
 */
export function useFeatureFlag({
  division,
  vertical,
  partner,
  environment,
  featureFlagUrl = 'https://www.launchsurely.sureapp.com/api/',
  flags,
  status,
  setFlags,
  setStatus,
  flagsToCheck,
}: TUseFeatureFlag) {
  const [isReady, setIsReady] = useState(status === 'SUCCESS');
  const [checkedFlags, setCheckedFlags] = useState<Record<string, boolean>>({});
  async function getFeatureFlags() {
    if (!isEmpty(flags) || ['SUCCESS', 'FAILED'].includes(status)) {
      setStatus('SUCCESS');
      setIsReady(true);
      return;
    }

    try {
      const data = await api.getFeatureFlag(
        { division, vertical, environment },
        { featureFlagUrl }
      );
      setFlags(data);
      setStatus('SUCCESS');
      setIsReady(true);
    } catch (err) {
      setStatus('FAILED');
    }
  }

  useEffect(function getFeatureFlagData() {
    getFeatureFlags();
  }, []);

  useEffect(
    function checkIfFeatureFlagsEnabled() {
      if (!isReady || !flagsToCheck?.length) {
        return;
      }

      const newlyCheckedFlags = flagsToCheck?.reduce(function (
        alreadyCheckedFlags,
        key
      ) {
        alreadyCheckedFlags[key as string] = isFeatureEnabled(key);
        return alreadyCheckedFlags;
      },
      checkedFlags);
      setCheckedFlags(newlyCheckedFlags);
    },
    [isReady, flagsToCheck]
  );

  function isFeatureEnabled(name: string) {
    const partnerKey = partnerMap[partner];
    return flags?.[name.toLowerCase()]?.[partnerKey] || false;
  }

  return { flags, isFeatureEnabled, status, isReady, checkedFlags };
}

export function createUseFeatureFlag({
  partner,
  environment,
  division,
  vertical,
  featureFlagUrl,
}: {
  partner: EPartner;
  environment: EEnvironment;
  division: string;
  vertical: string;
  featureFlagUrl?: string;
}) {
  return function ({
    flags,
    setFlags,
    status,
    setStatus,
    flagsToCheck,
  }: Pick<
    TUseFeatureFlag,
    'flags' | 'setFlags' | 'status' | 'setStatus' | 'flagsToCheck'
  >) {
    return useFeatureFlag({
      division,
      vertical,
      partner,
      environment,
      featureFlagUrl,
      flags,
      setFlags,
      status,
      setStatus,
      flagsToCheck,
    });
  };
}
